import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Search, Filter, LoaderCircle, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import config from '../../config';

const StatusBadge = ({ status }) => {
    // Replaced standard bright colors with the sophisticated, muted Mazda palette
    const statusStyles = {
        Approved: 'bg-[#111111] text-white border border-[#111111]',
        Pending: 'bg-transparent text-[#767676] border border-[#CCCCCC]',
        Rejected: 'bg-transparent text-[#8B0000] border border-[#8B0000]',
    };
    return (
        <span className={`px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-none ${statusStyles[status] || 'bg-transparent border border-[#E5E5E5] text-[#767676]'}`}>
            {status}
        </span>
    );
};

const InteractiveTable = () => {
    const [claimsData, setClaimsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'repair_date', direction: 'descending' });
    const [activeFilters, setActiveFilters] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Mock data fallback - increased to 155 to demonstrate pagination
    const mockData = Array.from({ length: 155 }).map((_, i) => ({
        id: `MOCK-${i}`, 
        vincd: `VIN${1000+i}`, 
        model: i % 2 === 0 ? 'MAZDA CX-5' : 'MAZDA 3', 
        claimAmount: (Math.random() * 1000).toFixed(2), 
        status: ['Approved', 'Pending', 'Rejected'][i % 3], 
        repair_date: '2025-01-15'
    }));

    useEffect(() => {
        const fetchClaims = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Simulating API call or using config
                const url = config.API_BASE_URL ? `${config.API_BASE_URL}/last-month-claims/` : null;
                if (!url) throw new Error("No API URL");
                
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                const dataWithIds = data.data.map((item, index) => ({ ...item, id: item.vincd + '-' + index }));
                setClaimsData(dataWithIds);
            } catch (e) {
                console.warn("Using mock data due to fetch error:", e);
                setClaimsData(mockData);
            } finally {
                setIsLoading(false);
            }
        };
        fetchClaims();
    }, []);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeFilters]);

    const processedData = useMemo(() => {
        let sortableItems = [...claimsData];
        if (searchTerm) {
            sortableItems = sortableItems.filter(item =>
                Object.values(item).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        if (activeFilters.length > 0) {
            sortableItems = sortableItems.filter(item => activeFilters.includes(item.status));
        }
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [claimsData, searchTerm, sortConfig, activeFilters]);

    // Pagination Logic
    const totalPages = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedData.slice(startIndex, startIndex + itemsPerPage);
    }, [processedData, currentPage]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const headers = [
        { key: 'vincd', label: 'VIN' },
        { key: 'model', label: 'Model' },
        { key: 'claimAmount', label: 'Claim Amount ($)' },
        { key: 'status', label: 'Status' },
        { key: 'repair_date', label: 'Repair Date' },
    ];

    // Generate page numbers for pagination controls
    const getPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        
        if (end - start < 4) {
            start = Math.max(1, end - 4);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="w-full bg-white border border-[#E5E5E5] rounded-none shadow-none">
            {/* Table Controls (Dark Tier) */}
            <div className="p-6 bg-[#111111] text-white flex flex-col sm:flex-row justify-between items-center gap-4 rounded-none">
                <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <h4 className="font-light text-xl tracking-[0.15em] uppercase">CLAIMS DATA</h4>
                </div>
                <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0">
                        <input
                            type="text"
                            placeholder="SEARCH..."
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-transparent border border-[#333333] rounded-none text-white text-xs uppercase tracking-widest focus:outline-none focus:border-white w-full transition-colors placeholder:text-[#767676]"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#767676]" />
                    </div>
                    <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="border border-[#333333] hover:border-white hover:bg-white/10 p-2 rounded-none transition-colors">
                        <Filter className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Filter Dropdown */}
            <AnimatePresence>
                {isFilterOpen && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="bg-[#F7F7F7] overflow-hidden border-b border-[#E5E5E5]">
                        <div className="p-6 flex gap-8">
                            {['Approved', 'Pending', 'Rejected'].map(status => (
                                <label key={status} className="flex items-center space-x-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={activeFilters.includes(status)} 
                                        onChange={() => setActiveFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status])} 
                                        className="rounded-none border-[#CCCCCC] text-[#111111] focus:ring-0 focus:ring-offset-0 bg-transparent h-4 w-4 cursor-pointer" 
                                    />
                                    <span className="text-xs font-semibold tracking-widest uppercase text-[#111111]">{status}</span>
                                </label>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Table Content */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-[#FFFFFF] border-b-2 border-[#111111]">
                        <tr>
                            <th className="px-6 py-4 w-12">
                                <input type="checkbox" className="rounded-none border-[#CCCCCC] text-[#111111] focus:ring-0 bg-transparent cursor-pointer" />
                            </th>
                            {headers.map(header => (
                                <th key={header.key} className="px-6 py-4 text-xs font-semibold text-[#111111] uppercase tracking-[0.15em] cursor-pointer hover:bg-[#F7F7F7] transition-colors" onClick={() => requestSort(header.key)}>
                                    <div className="flex items-center space-x-2">
                                        <span>{header.label}</span>
                                        {sortConfig.key === header.key && (sortConfig.direction === 'ascending' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E5E5E5]">
                        {isLoading ? (
                            <tr><td colSpan={6} className="p-12 text-center text-[#767676] uppercase tracking-widest text-sm"><LoaderCircle className="animate-spin h-6 w-6 mx-auto mb-4 text-[#111111]" />LOADING DATA...</td></tr>
                        ) : paginatedData.length === 0 ? (
                            <tr><td colSpan={6} className="p-12 text-center text-[#767676] uppercase tracking-widest text-sm">NO RECORDS FOUND.</td></tr>
                        ) : (
                            paginatedData.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-[#F7F7F7] transition-colors">
                                    <td className="px-6 py-4">
                                        <input type="checkbox" className="rounded-none border-[#CCCCCC] text-[#111111] focus:ring-0 bg-transparent cursor-pointer" />
                                    </td>
                                    <td className="px-6 py-4 text-sm font-mono text-[#767676]">{item.vincd}</td>
                                    <td className="px-6 py-4 text-sm text-[#111111]">{item.model}</td>
                                    <td className="px-6 py-4 text-sm font-medium text-[#111111]">{item.claimAmount}</td>
                                    <td className="px-6 py-4"><StatusBadge status={item.status} /></td>
                                    <td className="px-6 py-4 text-sm text-[#767676]">{item.repair_date}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="bg-[#FFFFFF] px-6 py-4 border-t border-[#E5E5E5] flex items-center justify-between">
                <div className="text-xs text-[#767676] tracking-wider uppercase">
                    SHOWING <span className="font-semibold text-[#111111]">{processedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> TO <span className="font-semibold text-[#111111]">{Math.min(currentPage * itemsPerPage, processedData.length)}</span> OF <span className="font-semibold text-[#111111]">{processedData.length}</span> RESULTS
                </div>
                <div className="flex space-x-1">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-none hover:bg-[#F7F7F7] disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-[#E5E5E5] transition-colors"
                    >
                        <ChevronLeft className="h-4 w-4 text-[#111111]" />
                    </button>
                    
                    {getPageNumbers().map(pageNum => (
                        <button 
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-none text-xs font-semibold tracking-widest transition-colors ${currentPage === pageNum ? 'bg-[#111111] text-white' : 'hover:bg-[#F7F7F7] text-[#767676] border border-transparent hover:border-[#E5E5E5]'}`}
                        >
                            {pageNum}
                        </button>
                    ))}

                    <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-2 rounded-none hover:bg-[#F7F7F7] disabled:opacity-30 disabled:cursor-not-allowed border border-transparent hover:border-[#E5E5E5] transition-colors"
                    >
                        <ChevronRight className="h-4 w-4 text-[#111111]" />
                    </button>
                </div>
            </div>
            
            {/* Action Buttons Footer */}
            <div className="bg-[#F7F7F7] p-6 flex flex-wrap justify-center sm:justify-end gap-4 border-t border-[#E5E5E5]">
                <button className="bg-transparent border border-[#111111] hover:bg-[#E5E5E5] text-[#111111] text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    RESET
                </button>
                <button className="bg-[#111111] hover:bg-[#333333] border border-[#111111] text-white text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    TRANSFER TO VOR
                </button>
                <button className="bg-[#111111] hover:bg-[#333333] border border-[#111111] text-white text-xs font-semibold py-3 px-8 rounded-none uppercase tracking-[0.15em] transition-colors">
                    GENERATE DATA FILE
                </button>
            </div>
        </div>
    );
};

export default InteractiveTable;